import { ArrowRight, MapPin, Phone, Mail } from "lucide-react";
import Globe, { REGIONAL_HUBS } from "./Globe";
import { Reveal, SectionTag } from "./Reveal";

const FLOW = ["Canada", "International Business Connections", "Ajemi Energy", "Nigeria Energy Supply Network", "Customers"];

const LOCATIONS = [
  {
    country: "Nigeria",
    label: "Head Office — Abuja",
    lines: ["Plot 8885, Cadastral Zone E", "Mabushi District, Abuja, Nigeria"],
    phone: "+234 915 198 1008",
    phone2: "+234 806 075 2250",
    testId: "presence-nigeria",
  },
  {
    country: "Canada",
    label: "International Operations — Calgary",
    lines: ["1018 Bridlemeadows SW", "Calgary, AB, Canada"],
    phone: "+1 403-427-0019",
    testId: "presence-canada",
  },
];

export default function PresenceSection() {
  return (
    <section id="presence" data-testid="presence-section" className="relative py-24 md:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <SectionTag number="16" label="Presence" />
          <h2 className="font-outfit text-3xl md:text-5xl font-extralight tracking-tight text-white">
            Rooted in Nigeria. <span className="text-gold">Connected internationally.</span>
          </h2>
          <p className="mt-6 text-white/60 text-base md:text-lg leading-relaxed max-w-lg">
            Nigeria is Ajemi&rsquo;s core operating market, with Canada forming part of our
            international procurement connectivity — serving customers and supply
            relationships across regions.
          </p>
          <div className="mt-10 grid sm:grid-cols-2 gap-5" data-testid="presence-locations">
            {LOCATIONS.map((loc) => (
              <div key={loc.country} className="glass-panel rounded-xl p-6" data-testid={loc.testId}>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gold" />
                  <span className="font-outfit text-lg text-white">{loc.country}</span>
                </div>
                <p className="text-[11px] tracking-[0.25em] uppercase text-white/40 mt-2">{loc.label}</p>
                <div className="mt-3 text-sm text-white/60 space-y-0.5">
                  {loc.lines.map((l) => (
                    <p key={l}>{l}</p>
                  ))}
                </div>
                {loc.phone && (
                  <span className="mt-3 flex flex-col gap-1.5">
                    {[loc.phone, loc.phone2].filter(Boolean).map((p) => (
                      <a key={p} href={`tel:${p.replace(/\s/g, "")}`} className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-gold transition-colors" data-testid={p === loc.phone ? `${loc.testId}-phone` : `${loc.testId}-phone-2`}>
                        <Phone className="w-3.5 h-3.5 text-gold" /> {p}
                      </a>
                    ))}
                  </span>
                )}
                {loc.email && (
                  <a href={`mailto:${loc.email}`} className="mt-3 inline-flex items-center gap-2 text-sm text-white/70 hover:text-gold transition-colors" data-testid={`${loc.testId}-email`}>
                    <Mail className="w-3.5 h-3.5 text-gold" /> {loc.email}
                  </a>
                )}
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div data-testid="presence-globe">
            <Globe hubs={REGIONAL_HUBS} offsetX={0} className="h-[380px] md:h-[520px] w-full" />
          </div>
        </Reveal>
        <Reveal delay={0.25}>
          <div className="mt-14 glass-panel rounded-2xl px-6 md:px-10 py-8" data-testid="presence-structure-flow">
            <div className="flex flex-wrap items-center gap-y-4">
              {FLOW.map((step, i) => (
                <span key={step} className="flex items-center">
                  <span className={`font-outfit text-sm md:text-base tracking-wide ${step === "Ajemi Energy" ? "text-gold" : "text-white/70"}`}>
                    {step}
                  </span>
                  {i < FLOW.length - 1 && <ArrowRight className="w-4 h-4 text-gold/50 mx-3 shrink-0" />}
                </span>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
